import { useState } from 'react';

const CREATE_SUBSCRIPTION_URL = 
  process.env.REACT_APP_CREATE_SUBSCRIPTION_URL || 
  'https://us-central1-asidorsmusic.cloudfunctions.net/createArtistSubscription';

const GET_SUBSCRIPTION_URL = 
  process.env.REACT_APP_GET_SUBSCRIPTION_URL || 
  'https://us-central1-asidorsmusic.cloudfunctions.net/getArtistSubscription';

const CANCEL_SUBSCRIPTION_URL = 
  process.env.REACT_APP_CANCEL_SUBSCRIPTION_URL || 
  'https://us-central1-asidorsmusic.cloudfunctions.net/cancelArtistSubscription';

export const useArtistSubscription = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Create subscription
  const createSubscription = async (userId, artistName, email, tier) => {
    setLoading(true);
    setError(null);

    try {
      if (!userId || !artistName || !email || !tier) {
        throw new Error('Missing required fields: userId, artistName, email, tier');
      }

      const response = await fetch(CREATE_SUBSCRIPTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          artistName,
          email,
          tier
        })
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to create subscription');
      }

      // Redirect to Paddle checkout
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return data;
      }

      throw new Error('No checkout URL received from server');
    } catch (err) {
      const errorMsg = err.message || 'Failed to create subscription';
      setError(errorMsg);
      console.error('Subscription creation error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get subscription status
  const getSubscription = async (userId) => {
    setLoading(true);
    setError(null);

    try {
      if (!userId) {
        throw new Error('userId is required');
      }

      const response = await fetch(`${GET_SUBSCRIPTION_URL}?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch subscription');
      }

      setSubscription(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch subscription';
      setError(errorMsg);
      console.error('Fetch subscription error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Cancel subscription
  const cancelSubscription = async (userId, subscriptionId) => {
    setLoading(true);
    setError(null);

    try {
      if (!userId || !subscriptionId) {
        throw new Error('userId and subscriptionId are required');
      }

      const response = await fetch(CANCEL_SUBSCRIPTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          subscriptionId
        })
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      setSubscription(null);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to cancel subscription';
      setError(errorMsg);
      console.error('Cancel subscription error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    subscription,
    createSubscription,
    getSubscription,
    cancelSubscription
  };
};
